import { IonButton } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { prepareDocumentFile } from '../lib/documents/documentSource.js';
import { getCaptureProvider, type CapturedPhoto } from '../lib/adapters/camera/index.js';

export type RegisterPhotoDraft = CapturedPhoto & { id: string };
/** The surrounding register draft persists this image before the record is submitted. */
export function RegisterPhoto({ value, onChange, disabled, required, onBusyChange }: {
  value: RegisterPhotoDraft | null; onChange: (photo: RegisterPhotoDraft | null) => void; disabled?: boolean;
  required?: boolean; onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; onBusyChange?.(false); };
  }, [onBusyChange]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function capture(source: 'camera' | 'gallery') {
    setBusy(true); onBusyChange?.(true); setError(null);
    try {
      const photo = await getCaptureProvider().takePhoto(source);
      if (photo && mounted.current) {
        if (photo.base64.length > 13_981_016) throw new Error('Scegli una foto inferiore a 10 MB');
        if (!photo.mime.startsWith('image/')) throw new Error('Seleziona una fotografia');
        const prepared = await prepareDocumentFile(photo.base64, photo.mime === 'image/jpg' ? 'image/jpeg' : photo.mime);
        if (prepared.sizeBytes > 10_485_760) throw new Error('Scegli una foto inferiore a 10 MB');
        if (mounted.current) onChange({ base64: prepared.b64, mime: prepared.mime, id: crypto.randomUUID() });
      }
    } catch (e) { if (mounted.current) setError(e instanceof Error ? e.message : 'Foto non acquisita'); }
    finally { if (mounted.current) { setBusy(false); onBusyChange?.(false); } }
  }
  return <section aria-label="Fotografia del controllo">
    <p>{required ? 'Foto del rapporto firmato obbligatoria' : 'Fotografia facoltativa'} · salvata anche senza rete</p>
    {value && <img alt="Foto allegata al controllo" src={`data:${value.mime};base64,${value.base64}`} style={{ maxWidth: '100%', maxHeight: 180 }} />}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <IonButton fill="outline" disabled={disabled || busy} onClick={() => void capture('camera')}>Scatta foto</IonButton>
      <IonButton fill="outline" disabled={disabled || busy} onClick={() => void capture('gallery')}>Scegli foto</IonButton>
      {value && <IonButton fill="clear" disabled={disabled || busy} onClick={() => onChange(null)}>Rimuovi dalla bozza</IonButton>}
    </div>
    {error && <p role="alert">{error}</p>}
  </section>;
}
